import { useState, useEffect, useMemo, useRef } from 'react'
import type { EquityQuote, MarketMovers, SearchSuggestion } from '../types/finance'
import { searchSymbols, getQuote, getMarketMovers, isUsingMockData } from '../lib/finance-api'
import { useWatchlists } from '../lib/watchlists-context'
import Lightbox from '../components/Lightbox'
import QuoteCard from '../components/finance/QuoteCard'
import WatchlistsCard from '../components/finance/WatchlistsCard'
import MarketMoversCard from '../components/finance/MarketMoversCard'
import TrendingNewsCard from '../components/finance/TrendingNewsCard'
import ResearchShell from '../components/research/ResearchShell'

export default function FinancialAppPage() {
  const [query, setQuery] = useState('')
  const [suggestions, setSuggestions] = useState<SearchSuggestion[]>([])
  const [showSuggestions, setShowSuggestions] = useState(false)
  const [quote, setQuote] = useState<EquityQuote | null>(null)
  const [quoteOpen, setQuoteOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [isMock, setIsMock] = useState(false)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const { isMock: listsMock } = useWatchlists()

  // Market pulse: movers load once, and trending news seeds off the same symbols.
  const [movers, setMovers] = useState<MarketMovers | null>(null)
  const [moversLoading, setMoversLoading] = useState(true)
  useEffect(() => {
    let cancelled = false
    getMarketMovers()
      .then((m) => { if (!cancelled) setMovers(m) })
      .catch(() => { /* card shows unavailable */ })
      .finally(() => { if (!cancelled) setMoversLoading(false) })
    return () => { cancelled = true }
  }, [])
  const trendingSeeds = useMemo<string[] | null>(() => {
    if (moversLoading) return null
    if (!movers || movers.source === 'mock') return []
    return [...movers.gainers.slice(0, 3), ...movers.losers.slice(0, 2)].map((m) => m.symbol)
  }, [movers, moversLoading])

  // Search autocomplete
  useEffect(() => {
    if (query.length < 1) {
      setSuggestions([])
      return
    }
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(async () => {
      try {
        const results = await searchSymbols(query)
        setSuggestions(results)
        setShowSuggestions(true)
      } catch {
        setSuggestions([])
      }
    }, 250)
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current) }
  }, [query])

  // Every security lookup opens in the lightbox.
  async function handleSearch(symbol: string) {
    setShowSuggestions(false)
    setQuery(symbol.toUpperCase())
    setError(null)
    setQuote(null)
    setQuoteOpen(true)
    try {
      setLoading(true)
      const result = await getQuote(symbol)
      setQuote(result)
      setIsMock(isUsingMockData())
    } catch {
      setError(`Failed to fetch quote for "${symbol}". Please try again.`)
      setQuoteOpen(false)
    } finally {
      setLoading(false)
    }
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Enter' && query.trim()) {
      handleSearch(query.trim())
    }
  }

  const showMock = isMock || listsMock

  return (
    <ResearchShell showMockBanner={showMock && !loading}>
      {/* Search bar */}
      <div className="relative mb-6">
        <div className="flex gap-2">
          <div className="relative flex-1">
            <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
              onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
              placeholder="Search by ticker symbol or company name (e.g. AAPL, Microsoft)"
              className="w-full bg-white dark:bg-slate-800 text-slate-900 dark:text-white text-sm rounded-xl pl-10 pr-4 py-3 border border-slate-300 dark:border-slate-600 focus:border-blue-500 outline-none placeholder-slate-400 dark:placeholder-slate-500"
            />
            {showSuggestions && suggestions.length > 0 && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 rounded-xl overflow-hidden z-50 shadow-xl">
                {suggestions.map((s) => (
                  <button
                    key={s.symbol}
                    onMouseDown={() => handleSearch(s.symbol)}
                    className="w-full flex items-center justify-between px-4 py-2.5 hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors text-left"
                  >
                    <div>
                      <span className="text-slate-900 dark:text-white font-medium text-sm">{s.symbol}</span>
                      <span className="text-slate-500 dark:text-slate-400 text-sm ml-2">{s.name}</span>
                    </div>
                    <span className="text-slate-500 text-xs">{s.exchange}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
          <button
            onClick={() => query.trim() && handleSearch(query.trim())}
            disabled={loading || !query.trim()}
            className="px-6 py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-200 dark:disabled:bg-slate-700 disabled:text-slate-500 text-white text-sm font-medium rounded-xl transition-colors"
          >
            {loading ? 'Loading...' : 'Search'}
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-500/10 border border-red-500/30 text-red-600 dark:text-red-400 rounded-xl px-4 py-3 mb-6 text-sm">
          {error}
        </div>
      )}

      {/* Market pulse first: biggest movers + trending news */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 mb-6">
        <MarketMoversCard movers={movers} loading={moversLoading} onSelect={handleSearch} />
        <TrendingNewsCard seedSymbols={trendingSeeds} />
      </div>

      {/* Unified watchlists — Favorites is the default list */}
      <WatchlistsCard onSelect={handleSearch} />

      {/* Security lookup lightbox */}
      <Lightbox open={quoteOpen} onClose={() => setQuoteOpen(false)}>
        {loading || !quote ? (
          <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl p-6 animate-pulse">
            <div className="h-8 bg-slate-200 dark:bg-slate-700 rounded w-1/4 mb-4" />
            <div className="h-12 bg-slate-200 dark:bg-slate-700 rounded w-1/3 mb-4" />
            <div className="h-44 bg-slate-200 dark:bg-slate-700 rounded mb-4" />
            <div className="grid grid-cols-4 gap-4">
              {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
                <div key={i} className="h-16 bg-slate-200 dark:bg-slate-700 rounded" />
              ))}
            </div>
          </div>
        ) : (
          <QuoteCard quote={quote} />
        )}
      </Lightbox>
    </ResearchShell>
  )
}
